import React, { useState, useEffect } from 'react'
import canada_img from "../images/canada.png";
import carleton_img from "../images/carleton.png";
import sov_prime_img from "../images/sov_prime.jpeg";
import uw_img from "../images/uwlogo123.png";
import amazon_img from "../images/amazon.png";
import story_img from "../images/story_logo.png";
import ramp_img from "../images/ramp.jpeg";

import "../styles/Experience.css"
import { Helmet } from "react-helmet";

const Experience = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideDistance, setSlideDistance] = useState(440);
  const [cardsToShow, setCardsToShow] = useState(3);
  
  const experiences = [
    {
      img: ramp_img,
      company: "Ramp",
      url: "https://ramp.com/blog/ramp-june-2025-funding",
      title: "Software Engineer Intern",
      duration: "SEPTEMBER 2025 - DECEMBER 2025",
      location: "New York City, NY",
      technologies: "Kafka, Temporal, PSQL, ClickHouse, Python",
      bullets: [
        "Developed Temporal workflows for Dead Letter Queue Retry solutions in Kafka Clusters with millions of messages/day", 
        "Engineered ETL workflows from PSQL to ClickHouse database, and improved a temporal workflow to support ETL Backfills. Used Kafka, Debezium and EC2 enrichers"
      ]
    },
    {
      img: story_img,
      company: "Story Protocol",
      url: "https://a16zcrypto.com/posts/article/investing-in-story/",
      title: "Software Engineer Intern",
      duration: "JANUARY 2025 - APRIL 2025",
      location: "Palo Alto, CA",
      technologies: "Go, PSQL, Redis, Temporal",
      bullets: [
        "Developed a Go backend for the company's website as well as implemented Multithreading and Redis caching strategies. Also monitored this API's health and performance using Sentry and Grafana.",
        "Created a Vector DB for user uploads with Temporal Workflows; Handled out-of-order events, back pressure and retries."
      ]
    },
    {
      img: amazon_img,
      company: "Amazon",
      url: "https://aws.amazon.com/",
      title: "Software Development Engineer Intern",
      duration: "JUNE 2024 - SEPTEMBER 2024",
      location: "Vancouver, BC",
      technologies: "OOP, Kotlin, TypeScript",
      bullets: [
        "Eliminated the need to recreate an EC2 instance for storage scaling for teams across 34 regions by creating a workflow to handle EBS volume scaling for all new AWS services using Java, Kotlin and TypeScript.",
        "Presented my solution design as a Software Design Document and demo to teams of stakeholders across 5 presentations.",

      ]
    },
    // {
    //   img: uw_img,
    //   company: "University of Waterloo",
    //   url: "https://uwaterloo.ca/",
    //   title: "Undergraduate Research Assistant — Machine Learning",
    //   duration: "JANUARY 2024 - APRIL 2024",
    //   location: "Waterloo, ON",
    //   technologies: "C++, CUDA, Python, Pandas",
    //   bullets: [
    //     "• Researched Parallelizing Zero-Knowledge Proofs of Machine Learning workloads to reduce proof overhead",
    //     "• Work with PyTorch and Linux Servers to compare and analyze ZK frameworks on Runtime, and Space Usage"
    //   ]
    // },
    {
      img: canada_img,
      company: "Federal Government of Canada",
      url: "https://www.canada.ca/en/public-services-procurement.html",
      title: "Junior Web Developer",
      duration: "MAY 2023 - SEPTEMBER 2023",
      location: "Ottawa, ON",
      technologies: "React.js, Express.js, Python, Docker",
      bullets: [
        "Programmed Full-Stack applications using React.js and Express.js for tasks such as Web Scraping and requesting federal government contract history for both internal and public use.",
      ]
    },
    {
      img: carleton_img,
      company: "Carleton University",
      url: "https://carleton.ca/",
      title: "Undergraduate Teaching Assistant",
      duration: "SEPTEMBER 2022 - APRIL 2023",
      location: "Ottawa, ON",
      technologies: "Java",
      bullets: [
        "Taught Java programming and libraries to over 100 students through weekly tutorials to advance their understanding of lectures, programming concepts, and to showcase Java functionality and frameworks.",
        "Also graded assignments and answered student questions regarding Java syntax, errors and library documentation to help with coursework."
      ]
    },
    {
      img: sov_prime_img,
      company: "Sovereign Prime",
      url: "",
      title: "Front-End Development Intern",
      duration: "MAY 2022 - SEPTEMBER 2022",
      location: "Ottawa, ON",
      technologies: "React.js",
      bullets: [
        "Programmed React components for a social media / online marketplace website."
      ]
    }
  ];

  const maxIndex = Math.max(0, experiences.length - cardsToShow);
  const totalSlides = maxIndex + 1;

  // Calculate slide distance and cards to show based on screen size
  const updateResponsiveSettings = () => {
    if (window.innerWidth <= 480) {
      setSlideDistance(370); // 350px card + gap
      setCardsToShow(1); // Show only 1 card on mobile
    } else if (window.innerWidth <= 768) {
      setSlideDistance(420); // 400px card + gap
      setCardsToShow(1); // Show only 1 card on tablets too if desired, or change to 2
    } else if (window.innerWidth <= 968) {
      setSlideDistance(0); // Vertical layout, no sliding
      setCardsToShow(1); // Show 1 card in vertical layout
    } else if (window.innerWidth <= 1200) {
      setSlideDistance(395); // 380px card + gap
      setCardsToShow(2); // Show 2 cards on smaller desktops
    } else {
      setSlideDistance(440); // 420px card + gap
      setCardsToShow(3); // Show 3 cards on larger screens
    }
  };

  useEffect(() => {
    updateResponsiveSettings();
    window.addEventListener('resize', updateResponsiveSettings);
    return () => window.removeEventListener('resize', updateResponsiveSettings);
  }, []);

  // Reset currentIndex if it's out of bounds after screen size change
  useEffect(() => {
    const newMaxIndex = Math.max(0, experiences.length - cardsToShow);
    if (currentIndex > newMaxIndex) {
      setCurrentIndex(newMaxIndex);
    }
  }, [cardsToShow, currentIndex, experiences.length]);

  const goNext = () => {
    if (currentIndex < maxIndex) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const goPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const visibleExperiences = experiences.slice(currentIndex, currentIndex + cardsToShow);

  return (
    <section className="section" id="experience">
      <Helmet>
        <html lang="en" />
        <meta
          name="description"
          content="Work Experience, Professional Experience, Professional Portfolio"
        />
      </Helmet>
      
      <div className="experience-header">
        <h1 className="title is-2 section-title">Experience</h1>
      </div>
      
      <div className="experience-carousel-container">
        <div className="experience-nav-buttons">
          <button 
            className="nav-button nav-button-prev" 
            onClick={goPrev}
            disabled={currentIndex === 0}
          >
            ←
          </button>
          <button 
            className="nav-button nav-button-next" 
            onClick={goNext}
            disabled={currentIndex === maxIndex}
          >
            →
          </button>
        </div>
        
        <div className="experience-carousel">
          <div 
            className="experience-carousel-track"
            style={{
              transform: `translateX(-${currentIndex * slideDistance}px)`
            }}
          >
            {experiences.map((exp, index) => (
              <div key={index} className="experience-card">
                <div className="experience-card-content">
                  <figure className="experience-logo">
                    <img src={exp.img} alt={`${exp.company} logo`} />
                  </figure>
                  <div className="experience-info">
                    <p className="experience-company">
                      {exp.url ? (
                        <a href={exp.url} target="_blank" rel="noopener noreferrer">
                          {exp.company}
                        </a>
                      ) : (
                        exp.company
                      )}
                    </p>
                    <p className="experience-title">{exp.title}</p>
                    <p className="experience-duration">{exp.duration}</p>
                    <p className="experience-location">{exp.location}</p>
                    <p className="experience-technologies">{exp.technologies}</p>
                    <div className="experience-content">
                      <ul>
                        {exp.bullets.map((bullet, bulletIndex) => (
                          <li key={bulletIndex}>{bullet}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="experience-progress">
          {Array.from({ length: totalSlides }, (_, index) => (
            <div 
              key={index}
              className={`progress-circle ${index === currentIndex ? 'active' : ''}`}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export default Experience;